import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Play, Square, Send, CheckCircle2, AlertTriangle, Shield,
  FileText, Clock, Upload, Download, Trash2, Key, Loader2,
  Monitor, ChevronRight, X, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OpenClawTab = ({ blueprintId }) => {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sideTab, setSideTab] = useState('claw'); // claw, files, audit, vault
  const [task, setTask] = useState('');
  const [plan, setPlan] = useState(null);
  const [runId, setRunId] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [files, setFiles] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [vaultKeys, setVaultKeys] = useState([]);
  const [executionResult, setExecutionResult] = useState(null);
  const [showVaultModal, setShowVaultModal] = useState(false);
  const [vaultForm, setVaultForm] = useState({ key: '', value: '' });
  const [domainInput, setDomainInput] = useState('');

  useEffect(() => { fetchWorkspace(); }, [blueprintId]);

  const fetchWorkspace = async () => {
    try {
      const res = await axios.get(`${API}/openclaw/workspaces`);
      const ws = res.data.find(w => w.blueprint_id === blueprintId);
      if (ws) {
        setWorkspace(ws);
        fetchRuns(ws.id);
        fetchFiles(ws.id);
        fetchAudit(ws.id);
        fetchVault(ws.id);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createWorkspace = async () => {
    try {
      const res = await axios.post(`${API}/openclaw/workspaces`, { blueprint_id: blueprintId, allowed_domains: [], max_actions_per_run: 50 });
      setWorkspace(res.data);
      toast.success('Workspace created');
    } catch (err) { toast.error('Failed to create workspace'); }
  };

  const toggleWorkspace = async () => {
    if (!workspace) return;
    const action = workspace.status === 'running' ? 'stop' : 'start';
    try {
      await axios.post(`${API}/openclaw/workspaces/${workspace.id}/${action}`);
      setWorkspace(prev => ({ ...prev, status: action === 'start' ? 'running' : 'stopped' }));
      toast.success(`Workspace ${action}ed`);
    } catch (err) { toast.error('Failed'); }
  };

  const fetchRuns = async (wsId) => { try { const r = await axios.get(`${API}/openclaw/runs/${wsId}`); setRuns(r.data); } catch {} };
  const fetchFiles = async (wsId) => { try { const r = await axios.get(`${API}/openclaw/files/${wsId}`); setFiles(r.data); } catch {} };
  const fetchAudit = async (wsId) => { try { const r = await axios.get(`${API}/openclaw/audit/${wsId}`); setAuditLog(r.data); } catch {} };
  const fetchVault = async (wsId) => { try { const r = await axios.get(`${API}/openclaw/vault/${wsId}`); setVaultKeys(r.data); } catch {} };

  const handlePlan = async () => {
    if (!task.trim() || !workspace) return;
    setPlanning(true);
    setPlan(null);
    setExecutionResult(null);
    try {
      const res = await axios.post(`${API}/openclaw/plan`, { workspace_id: workspace.id, task: task.trim() });
      setPlan(res.data.plan);
      setRunId(res.data.run_id);
      toast.success('Plan generated');
    } catch (err) { toast.error('Planning failed'); }
    finally { setPlanning(false); }
  };

  const handleExecute = async (approvedSteps = []) => {
    if (!runId) return;
    setExecuting(true);
    try {
      const res = await axios.post(`${API}/openclaw/execute`, { run_id: runId, approved_steps: approvedSteps });
      setExecutionResult(res.data);
      fetchRuns(workspace.id);
      fetchAudit(workspace.id);
      toast.success(`Executed ${res.data.actions_executed} actions`);
    } catch (err) { toast.error('Execution failed'); }
    finally { setExecuting(false); }
  };

  const handleKill = async () => {
    if (!runId) return;
    try {
      await axios.post(`${API}/openclaw/kill/${runId}`);
      setExecuting(false);
      toast.success('Run killed');
      fetchRuns(workspace.id);
    } catch (err) { toast.error('Kill failed'); }
  };

  const addDomain = async () => {
    if (!domainInput.trim() || !workspace) return;
    const updated = [...(workspace.allowed_domains || []), domainInput.trim()];
    await axios.patch(`${API}/openclaw/workspaces/${workspace.id}`, { allowed_domains: updated });
    setWorkspace(prev => ({ ...prev, allowed_domains: updated }));
    setDomainInput('');
  };

  const removeDomain = async (d) => {
    const updated = (workspace.allowed_domains || []).filter(x => x !== d);
    await axios.patch(`${API}/openclaw/workspaces/${workspace.id}`, { allowed_domains: updated });
    setWorkspace(prev => ({ ...prev, allowed_domains: updated }));
  };

  const handleVaultSave = async () => {
    if (!vaultForm.key || !vaultForm.value || !workspace) return;
    try {
      await axios.post(`${API}/openclaw/vault`, { workspace_id: workspace.id, key: vaultForm.key, value: vaultForm.value });
      toast.success('Credential stored');
      setShowVaultModal(false);
      setVaultForm({ key: '', value: '' });
      fetchVault(workspace.id);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;

  if (!workspace) {
    return (
      <div className="text-center py-12">
        <Monitor className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
        <h3 className="font-bold mb-2">No Workspace</h3>
        <p className="text-sm text-muted-foreground mb-4">Create a workspace to use OpenClaw browser automation.</p>
        <Button onClick={createWorkspace} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm" data-testid="create-workspace-btn">Create Workspace</Button>
      </div>
    );
  }

  return (
    <div className="flex h-[600px] bg-[#080808] border border-white/5" data-testid="openclaw-tab">
      {/* Main Panel: Desktop View */}
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="h-10 border-b border-white/5 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <span className={`w-2 h-2 rounded-full ${workspace.status === 'running' ? 'bg-emerald-500 animate-pulse' : 'bg-white/20'}`} />
            <span className="text-xs font-mono text-muted-foreground">{workspace.status.toUpperCase()}</span>
            <span className="text-[10px] text-muted-foreground">|</span>
            <span className="text-[10px] text-muted-foreground font-mono">{(workspace.allowed_domains || []).join(', ') || 'No domain restrictions'}</span>
          </div>
          <div className="flex items-center gap-2">
            {executing && <Button onClick={handleKill} size="sm" className="bg-red-500 text-white hover:bg-red-400 rounded-sm h-6 text-[10px] px-2" data-testid="kill-btn"><Square className="w-3 h-3 mr-0.5" /> Kill</Button>}
            <Button onClick={toggleWorkspace} size="sm" variant="outline" className="border-white/10 rounded-sm h-6 text-[10px] px-2" data-testid="toggle-ws-btn">
              {workspace.status === 'running' ? <><Square className="w-3 h-3 mr-0.5" /> Stop</> : <><Play className="w-3 h-3 mr-0.5" /> Start</>}
            </Button>
          </div>
        </div>

        {/* Desktop area (simulated view) */}
        <div className="flex-1 bg-[#0a0a0a] flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.02) 1px, transparent 0)', backgroundSize: '20px 20px' }} />
          {workspace.status !== 'running' ? (
            <div className="text-center z-10">
              <Monitor className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-20" />
              <p className="text-sm text-muted-foreground">Workspace stopped. Start to begin.</p>
            </div>
          ) : executionResult ? (
            <div className="w-full h-full p-4 overflow-auto z-10">
              <div className="max-w-lg mx-auto space-y-2">
                <p className="text-xs text-emerald-400 font-mono mb-3">Execution Trace — {executionResult.actions_executed} actions</p>
                {(executionResult.steps || []).map((s, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2 border text-xs ${s.status === 'completed' ? 'border-emerald-500/20 bg-emerald-500/5' : s.status === 'awaiting_approval' ? 'border-amber-500/20 bg-amber-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                    <span className="font-mono text-muted-foreground w-5">{s.step}</span>
                    <div className="flex-1">
                      <span className="font-mono text-emerald-400">{s.action}</span>
                      <span className="text-muted-foreground ml-2">{s.description?.slice(0, 50)}</span>
                      {s.requires_approval && <span className="ml-2 text-[9px] px-1 bg-amber-500/20 text-amber-400">APPROVAL</span>}
                    </div>
                    <span className={`text-[10px] ${s.status === 'completed' ? 'text-emerald-400' : s.status === 'awaiting_approval' ? 'text-amber-400' : 'text-red-400'}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : plan ? (
            <div className="w-full h-full p-4 overflow-auto z-10">
              <div className="max-w-lg mx-auto">
                <p className="text-xs text-violet-400 font-mono mb-1">Action Plan</p>
                <p className="text-xs text-muted-foreground mb-3">{plan.plan_summary}</p>
                <div className="space-y-1.5">
                  {(plan.steps || []).map((s, i) => (
                    <div key={i} className={`flex items-start gap-2 p-2 border text-xs ${s.requires_approval ? 'border-amber-500/20 bg-amber-500/5' : 'border-white/5'}`}>
                      <span className="font-mono text-muted-foreground w-5">{s.step}</span>
                      <div className="flex-1">
                        <span className="font-mono text-emerald-400">{s.action}</span>
                        <span className="text-muted-foreground ml-2">{s.description?.slice(0, 60)}</span>
                      </div>
                      {s.requires_approval && <span className="text-[9px] px-1 bg-amber-500/20 text-amber-400">REQUIRES APPROVAL</span>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => handleExecute()} disabled={executing} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-8 text-xs flex-1" data-testid="approve-all-btn">
                    {executing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" /> Approve All & Execute</>}
                  </Button>
                  <Button onClick={() => { setPlan(null); setRunId(null); }} variant="outline" className="border-white/10 rounded-sm h-8 text-xs"><X className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center z-10">
              <p className="text-sm text-muted-foreground">Enter a task in the command panel to generate a plan.</p>
            </div>
          )}
        </div>
      </div>

      {/* Side Panel */}
      <div className="w-80 border-l border-white/5 flex flex-col">
        {/* Tabs */}
        <div className="flex border-b border-white/5">
          {[
            { key: 'claw', label: 'OpenClaw' },
            { key: 'files', label: 'Files' },
            { key: 'audit', label: 'Audit' },
            { key: 'vault', label: 'Vault' },
          ].map(t => (
            <button key={t.key} onClick={() => setSideTab(t.key)} className={`flex-1 py-2 text-[10px] font-mono uppercase transition-colors ${sideTab === t.key ? 'text-emerald-400 border-b border-emerald-500' : 'text-muted-foreground hover:text-white'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {/* CLAW TAB */}
          {sideTab === 'claw' && (
            <div className="p-3 space-y-3">
              {/* Domains */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase mb-1">Allowed Domains</p>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {(workspace.allowed_domains || []).map(d => (
                    <span key={d} className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground flex items-center gap-1">
                      {d} <button onClick={() => removeDomain(d)} className="text-red-400 hover:text-red-300"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input value={domainInput} onChange={e => setDomainInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDomain()} placeholder="hubspot.com" className="flex-1 bg-black/30 border-white/10 rounded-sm h-7 text-[10px]" data-testid="domain-input" />
                  <Button onClick={addDomain} size="sm" variant="outline" className="border-white/10 rounded-sm h-7 text-[10px] px-2">Add</Button>
                </div>
              </div>

              {/* Task Input */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase mb-1">Task</p>
                <Textarea value={task} onChange={e => setTask(e.target.value)} placeholder="Update all leads in HubSpot with status Qualified..." className="bg-black/30 border-white/10 rounded-sm text-xs min-h-[60px]" data-testid="task-input" />
                <Button onClick={handlePlan} disabled={planning || !task.trim() || workspace.status !== 'running'} className="w-full mt-1.5 bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-8 text-xs" data-testid="generate-plan-btn">
                  {planning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Generate Plan
                </Button>
              </div>

              {/* Run History */}
              <div>
                <p className="text-[10px] text-muted-foreground uppercase mb-1">Run History</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {runs.slice(0, 5).map(r => (
                    <div key={r.id} className="flex items-center justify-between py-1.5 px-2 bg-black/20 border border-white/5 text-[10px]">
                      <span className="text-muted-foreground truncate">{r.task_prompt?.slice(0, 25)}</span>
                      <span className={`font-mono ${r.status === 'completed' ? 'text-emerald-400' : r.status === 'killed' ? 'text-red-400' : 'text-amber-400'}`}>{r.status}</span>
                    </div>
                  ))}
                  {runs.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-2">No runs yet</p>}
                </div>
              </div>
            </div>
          )}

          {/* FILES TAB */}
          {sideTab === 'files' && (
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-muted-foreground uppercase">Workspace Files</p>
              </div>
              {files.map(f => (
                <div key={f.id} className="flex items-center justify-between py-1.5 px-2 bg-black/20 border border-white/5 text-[10px]">
                  <div className="flex items-center gap-1.5"><FileText className="w-3 h-3 text-muted-foreground" /><span>{f.filename}</span></div>
                  <span className="text-muted-foreground">{f.size_bytes}B</span>
                </div>
              ))}
              {files.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-4">No files</p>}
            </div>
          )}

          {/* AUDIT TAB */}
          {sideTab === 'audit' && (
            <div className="p-3 space-y-1.5">
              <p className="text-[10px] text-muted-foreground uppercase mb-2">Action Timeline</p>
              {auditLog.slice(0, 20).map(a => (
                <div key={a.id} className="flex items-start gap-2 py-1.5 border-b border-white/5 text-[10px]">
                  <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${a.status === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {a.status === 'success' ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-emerald-400">{a.action_type}</span>
                    {a.requires_approval && <span className="ml-1 text-amber-400">[APPROVAL]</span>}
                    <p className="text-muted-foreground truncate">{a.url}</p>
                  </div>
                  <span className="text-muted-foreground flex-shrink-0">{new Date(a.timestamp).toLocaleTimeString()}</span>
                </div>
              ))}
              {auditLog.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-4">No actions</p>}
            </div>
          )}

          {/* VAULT TAB */}
          {sideTab === 'vault' && (
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-muted-foreground uppercase">Secure Credentials</p>
                <Button onClick={() => setShowVaultModal(true)} size="sm" variant="outline" className="border-white/10 rounded-sm h-6 text-[10px] px-2" data-testid="add-vault-btn"><Key className="w-3 h-3 mr-0.5" /> Add</Button>
              </div>
              {vaultKeys.map(v => (
                <div key={v.key} className="flex items-center justify-between py-1.5 px-2 bg-black/20 border border-white/5 text-[10px]">
                  <div className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-emerald-400" /><span className="font-mono">{v.key}</span></div>
                  <button onClick={async () => { await axios.delete(`${API}/openclaw/vault/${workspace.id}/${v.key}`); fetchVault(workspace.id); }} className="text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {vaultKeys.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-4">No credentials stored</p>}
            </div>
          )}
        </div>
      </div>

      {/* Vault Modal */}
      <Dialog open={showVaultModal} onOpenChange={setShowVaultModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Add Credential</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={vaultForm.key} onChange={e => setVaultForm({ ...vaultForm, key: e.target.value })} placeholder="Key (e.g. hubspot_api)" className="bg-black/50 border-white/10 rounded-sm text-sm" data-testid="vault-key" />
            <Input type="password" value={vaultForm.value} onChange={e => setVaultForm({ ...vaultForm, value: e.target.value })} placeholder="Value (encrypted at rest)" className="bg-black/50 border-white/10 rounded-sm text-sm" data-testid="vault-value" />
            <Button onClick={handleVaultSave} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm text-sm" data-testid="vault-save">Store Securely</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenClawTab;
